<script lang="ts">
    import route from '../Util/routes';
    import { createSettingsSubmitHandler, round } from '@/Util/utils';
    import { format } from 'date-fns'
    import TrainingLoad from './TrainingLoad.svelte';
    import ActivitiesDashLegacy from '../ActivitiesDashLegacy.svelte';
    import Modal from '../Bootstrap/Modal.svelte';
    import { Button } from '@/lib/components/ui/button';
    import { type ComponentType, onMount } from 'svelte';
    import FitnessSignature from './FitnessSignature.svelte';

    export let component: ComponentType = TrainingLoad
    export let table = [];
    export let endpoint = route('UserController@postTrainingLoad');
    export let title = "Manual Training Loads"
    export let selected_exercise = null;
    export let activityPickerTitle = "Choose Activity to Change";

    //@ts-expect-error
    const Xert = window.Xert;
    const editableStates = ['editing', 'new'];
    // Whether an entry has been added or opened for editing, which Save All then saves (only an entry whose values differ is sent)
    export let changed = false;
    $: changed = _table.some(e => editableStates.includes(e?.state));

    export let newEntry: (...args)=>any = async(activity)=>{
        let tl = {pp:0, hie:0, ftp:0, auto_calc:true};
        await window.fetch("/getTrainingLoad?path="+activity?.path).then(res=>res.json()).then(data=>{if(data){ tl = data; tl.auto_calc = true;}});
        return {path:activity?.path,  date:activity?.start_date, name:activity?.name, tl, state:"new"}
    }

    let _table = [];
    let excludeExercisesSet;
    onMount(()=>{
        _table = table.map(e=>{return {...e, state:"saved"}}) ?? [];
        excludeExercisesSet = new Set(table.map(e=>e.path));
    })

    let _activityPickerModal = null;
    export async function activitySelect(activity = null){      
        if(_table?.find((e)=>activity.path == e.path)){
            Xert.danger('This activity has already been selected!');
            return;
        }
        _activityPickerModal?.close();
        _table.push( await newEntry(activity)); 
        _table = _table;
        excludeExercisesSet.add(activity.path)
    }

    export let getBody = (entry) => {
        let body = entry.input?.getBody(); body.path = entry.path; return {tl:body};
    }
    export let _entrySubmitHandler = createSettingsSubmitHandler(endpoint, getBody); 

    //TODO: would be preferable to send all the "getBody"s of the inputs in one request, would reduce network load
    //requires update endpoint to iterate through a list of bodies in the request
    export function saveAll(){ 
        _table.filter(e=>e.state!="saved").forEach(e=>editToggleClick(e));
    }

    export function deleteEntry(entry){
        if (entry?.state == "saving") return;   // not while its save is on its way
        if(entry?.state == "editing"){
            if(entry.input instanceof TrainingLoad){
                entry.tl = entry.oldTl;
            }
            else if (entry.input instanceof FitnessSignature){
                entry.fs = entry.savedFs ?? entry.fs;
            }
            entry.state = "saved";
            _table=_table;
            return
        }
        _table = _table.filter(e=> e.path != entry.path);
        excludeExercisesSet.delete(entry.path)
        excludeExercisesSet = excludeExercisesSet
        if(entry?.state == "new"){
            return;
        }
        _entrySubmitHandler(entry) //requests auto_calc for detail // auto_calc == true
    }
    

    export async function editToggleClick(entry){
        if (entry?.state == "saving") return;   // one save at a time
        if(!entry?.state || entry.state=="none" || entry.state=="saved"){
            if(entry.input instanceof TrainingLoad){
                entry.oldTl = {...entry.tl}
            }
            // A signature row shows entry.fs again whenever the table refreshes, so entry.fs follows what is typed while it is
            // saved; what the server has is kept apart, to compare against
            else if (entry.input instanceof FitnessSignature && entry.savedFs === undefined) {
                entry.savedFs = {...entry.fs}
            }
            entry.state = "editing"
        }
        else{
            let currentValues = entry.input.getEditableValues();
            let oldEntry = null;
            if(entry.input instanceof TrainingLoad){
                oldEntry =  entry?.oldTl;
            }
            else if (entry.input instanceof FitnessSignature){
                let oldfs = entry.savedFs ?? entry.fs;
                oldEntry = { pp:oldfs.pp, hie:oldfs?.hie ?? round(oldfs.atc / 1000, 1), ftp:oldfs.ftp};
            }
            if(!entry.input.validate() ){
                return;
            }
            else if(entry.state == "new" || !(currentValues.pp == oldEntry?.pp && currentValues.hie == oldEntry?.hie && currentValues.ftp == oldEntry?.ftp)){
                // Saved only once the server has taken it: a failed or turned-down save stays open to be saved again. While it is
                // on its way the row can't be edited (what is sent is read as the request starts), so nothing typed meanwhile is lost.
                const before = entry.state;
                // Kept on screen through the refreshes below, saved or not
                if (entry.input instanceof FitnessSignature) entry.fs = {...currentValues};
                const request = _entrySubmitHandler(entry);
                entry.state = "saving";
                _table = _table;
                const ok = await request.catch(() => false);
                if (ok === false) {
                    entry.state = before;
                    _table = _table;
                    return;
                }
                // The values the server now has, for the next edit to compare against
                if (entry.input instanceof FitnessSignature) entry.savedFs = {...currentValues};
            }
            entry.state="saved";
        }
        _table = _table;
    }

</script>


<div class="subtitle m-2 flex flex-row justify-between">
    <slot name="title"><h4>{title}</h4></slot>
    <div class="training-load-controls grow justify-end float-right" >
        <button class="btn" on:click={()=>{_activityPickerModal?.show();}}><i class="fa fa-light fa-lg fa-plus" /> Add</button>
    </div>
</div>
<div class="training-load-table flex flex-col m-2">
    <div class="training-load-row " >
        <span class="date bold table-header">Date</span>
        <span class="name table-header">Activity</span>
        <div class=" table-header training-load" >
            <svelte:component this={component} smallSize={true} hideTitle={true} hideLabels={false} hideUnits={true} hideInputs={true} lockable={false}></svelte:component>
        </div>
        <div class=" table-header training-load-controls flex flex-row" >
            <span class="table-header bold">Actions</span>
        </div>
    </div>
    <hr style="margin: 5px 0px;">
    {#each _table as entry (entry.path)}
        <div class="training-load-row " >
                <!-- <input value= disabled readonly> -->
                <div class="date" ><span class="bold">{format(new Date(entry?.date),"yyyy-MM-dd" )}</span></div> 
                <div class="name" ><a href={"/activities/" + entry?.path} target="_blank" rel="noopener noreferrer" >{entry?.name ?? ""}</a></div>
            <!-- </div> -->
            <div class="training-load" >
                <svelte:component this={component} bind:this={entry.input} {entry} smallSize={true} lockable={false} auto_calc={!editableStates.includes(entry?.state)} readonly={!editableStates.includes(entry?.state)}  hideTitle={true} hideLabels={true} hideUnits={true}></svelte:component>
            </div>
            <div class="training-load-controls">
                <div on:keypress={()=>{}} on:click={()=>{deleteEntry(entry)}}><i class={"fa fa-light fa-lg fa-"+( entry?.state != "editing"?"trash-can":"undo")}></i></div>
                {#if !editableStates.includes(entry?.state)}<div on:keypress={()=>{}}  on:click={()=>{editToggleClick(entry)}}><i class="fa fa-light fa-lg fa-edit"></i></div>
                {:else}
                <Button size="sm" on:click={()=>{editToggleClick(entry)}}>Save</Button>
                {/if}
            </div>
        </div>
        <hr style="margin: 5px 0px;">
    {/each}
</div>

<Modal maxWidthStyleString={"80%"} bind:this={_activityPickerModal} on:selectAndSave={()=>{}} >
    <h4 slot="title"><span class="bold">{activityPickerTitle}</span></h4>
    <ActivitiesDashLegacy dataSource={"/activities/dashboard"} {excludeExercisesSet} bind:selected_exercise isActivityPicker={true} defaultSort={1} onSelect={async (activity)=>{await activitySelect(activity)}} excludeManual={component == TrainingLoad ?"tl":"fs"}></ActivitiesDashLegacy>
</Modal>

<style lang="scss">

    @container training-load-container (max-width: 723px) {

        .table-header{
            display: none !important;
        }

        .training-load-controls{
            grid-row: 1 / span 2  !important;
            grid-column: 6 / span 1  !important;
        }
        .training-load-activity-details {
            gap:15px;
        }

        .name{
            grid-row: 2 / span 1 !important;
            grid-column: 1 /span 4 !important;
        }
        .date{
            grid-row: 1 / span 1 !important;
            grid-column: 1 /span 4 !important;
        }

        .training-load-activity-details {
            grid-row: 1 / span 2 !important;
            grid-column: 1 /span 6 !important;
        }


        .training-load {
            grid-row: 3 / span 4 !important;
            grid-column: 1 / span 6 !important;
        }

    }

    .training-load-controls{
        align-items: center;
        display: flex;
        gap:8px;
        grid-column: 6 / span 1 ;
        grid-row: 1 / span 6;
  
        div{
            aspect-ratio: 1 /1;
            height: 40px;
            display: flex;
            justify-content: center;
            i {
                height: min-content;
                margin: auto 0;
                &:hover {
                    color: var(--color-xert-teal) !important;
                }
            }
        }
    }

    .tl-row {
        flex-grow:1;
        display:flex;
        flex-direction:row;
        flex-wrap:wrap;
        gap:10px;
    }


    .name{
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        grid-column: 2 / span 1;
        grid-row: 1 / span 6;
        align-content: center;
        padding: 0 4px;
    }
    .date{
        padding:4px;
        grid-column: 1 / span 1;
        grid-row: 1 / span 6;
        align-content: center;
    }
    .training-load-activity-details {
        display:flex;
        flex-direction: row;
        margin:4px;
        margin-top:auto;
        flex-grow:1;
        flex-wrap:nowrap;
        grid-column: 1 / span 2;
        grid-row: 1 / span 6;
        // justify-content: flex-end;
        // align-items: flex-end;
        overflow: hidden;
        text-overflow: ellipsis;
        height:100%;

    }

    .training-load-row {
        container: training-load-container;
        display:grid;
        grid-template-columns: 96px repeat(4, 1fr) 108px;
        grid-template-rows: repeat(6, 1fr);
        height:fit-content;
    }


    .training-load {
        display:flex;
        justify-content: flex-end;
        align-items: center;
        grid-column: 3 / span 3;
        grid-row: 1 / span 6;
    }

    .form-group {
        margin:0;
    }

</style>